// Visual constants of the screw connector's react-three-fiber viewer, shared by the scene, its dimensions and its
// reference plane.
import { linearColor, type ViewName } from "../tc3dViewer";

// Same white canvas as the lock calculator's viewer.
export const VIEWER_BACKGROUND = "#ffffff";

// The canvas renders like the report viewer (linear output, no tone mapping), so the hex values are the on-screen
// colours: they are set through linearColor, not converted from sRGB.
// Dimensions and plane in the lock calculator's palette (its config/colors.ts).
export const QUOTE_COLORS = {
  dimensionLine: linearColor(0x1e293b),
  extensionLine: linearColor(0x64748b),
  arrow: linearColor(0x1e293b),
  labelText: linearColor(0x0f172a),
  labelBackground: linearColor(0xf1f4f7),
  forceLabelText: linearColor(0x991b1b),
  forceLabelBackground: linearColor(0xfef2f2),
} as const;

// Dimensions as the lock calculator draws them (its QUOTE_DEFAULTS), in metres and scaled down to the screw
// connection, whose sizes (widths, thicknesses) are much shorter than the lock's beams. World sizes: they scale with the
// camera like the geometry and are hidden by what stands in front of them.
export const QUOTE = {
  extensionOvershoot: 0.008,
  // between the dimension line and the label's edge, outwards
  labelGap: 0.006,
  arrowLength: 0.014,
  arrowWidth: 0.006,
  // on a short dimension (a thin element's thickness) each arrow takes at most this fraction of the line
  arrowMaxFraction: 0.25,
  arrowConeSegments: 12,
  labelSize: 0.02,
  forceLabelSize: 0.025,
  // chip padding as a fraction of the label size; the chip sits this far behind the text (no z-fighting)
  labelChipPaddingRatio: 0.28,
  labelChipDepth: 0.0004,
} as const;

// The lock calculator's reference plane ("the sheet of paper"): a 3 m sheet with 100 mm squares under the
// connection, wider when the scene is.
export const GRID = {
  minSize: 3,
  cell: 0.1,
  lineOpacity: 0.8,
  // floor and border just below the lines (no z-fighting)
  floorDrop: 0.001,
  borderDrop: 0.0005,
  lineColor: linearColor(0xb6bfc9),
  floorColor: linearColor(0xffffff),
  borderColor: linearColor(0x94a3b0),
} as const;

// The design system ships the font with a content hash: the glob resolves whichever version is installed.
const LABEL_FONT = Object.values(
  import.meta.glob<string>("/node_modules/@rothoblaas/design-system/dist/fonts/MuseoSans_700-*.otf", {
    query: "?url",
    import: "default",
    eager: true,
  }),
)[0];
export const LABEL_FONT_URL: string | undefined = LABEL_FONT;

// Camera directions (units) of the View menu. "iso" is the 3/4 perspective of the old viewer.
export const VIEW_DIRECTIONS: Record<ViewName, [number, number, number]> = {
  iso: [0.6247, 0.4685, 0.6247],
  front: [0, 0, 1],
  top: [0, 1, 0],
  side: [1, 0, 0],
};

// Framing: the camera fits the whole connection with some air around.
export const FRAME_MARGIN = 1.2;
export const MIN_SCENE_RADIUS = 0.012;
// Orthographic camera: its distance does not change the framing, only what the near plane cuts. Far enough to keep
// the whole reference sheet in front of the camera.
export const CAMERA_DISTANCE = 10;
